import React, { useState, useEffect } from 'react';
import { showSuccess, showError } from '../utils/alertas';

const IS_PROD = true;
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';

const NovaETEModal = ({ isOpen, onClose, selectedLocation, onSaveSuccess, listaGerencias }) => {
    const [isLoading, setIsLoading] = useState(false);
    
    // Formulário
    const [formData, setFormData] = useState({
        nome: '',
        gerencia_id: '',
        endereco: '',
        municipio_id: '',
        latitude: '',
        longitude: '',
        vazao_atual_ls: '',
        vazao_futura_ls: '',
        populacao_atual: '',
        populacao_futura: '',
        controle_vazao_entrada: 'NI',
        controle_vazao_saida: 'NI',
        corpo_hidrico: '',
        classe_rio: '',
        descricao_curta: '',
        descricao_completa: '',
        historia_narrativa: '',
        texto_diferenciais: '',
        texto_beneficios: '',
        texto_sustentabilidade: '',
        exibir_planejamento_publico: '0'
    });

    const [imagemCapa, setImagemCapa] = useState(null);
    const [imagemPreview, setImagemPreview] = useState(null);
    const [municipiosDB, setMunicipiosDB] = useState([]);
    const [faltantes, setFaltantes] = useState([]);

    // Fetch todos os municípios para o select
    useEffect(() => {
        if (isOpen) {
            fetch(`${API_BASE_URL}/municipios`)
                .then(res => res.json())
                .then(data => setMunicipiosDB(data))
                .catch(err => console.error("Erro ao carregar municípios:", err));
        }
    }, [isOpen]);

    // Atualiza Lat/Lng quando clicar no mapa
    useEffect(() => {
        if (selectedLocation) {
            setFormData(prev => ({
                ...prev,
                latitude: selectedLocation.lat.toFixed(8),
                longitude: selectedLocation.lng.toFixed(8)
            }));
        }
    }, [selectedLocation]);

    // Limpar formulário ao fechar/abrir
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                nome: '', gerencia_id: '', endereco: '', municipio_id: '', latitude: '', longitude: '',
                vazao_atual_ls: '', vazao_futura_ls: '', populacao_atual: '', populacao_futura: '',
                controle_vazao_entrada: 'NI', controle_vazao_saida: 'NI', corpo_hidrico: '', classe_rio: '',
                descricao_curta: '', descricao_completa: '', historia_narrativa: '', texto_diferenciais: '',
                texto_beneficios: '', texto_sustentabilidade: '', exibir_planejamento_publico: '0'
            });
            setImagemCapa(null);
            setImagemPreview(null);
            setFaltantes([]);
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Remove do array de faltantes quando o usuário digita algo
        if (faltantes.includes(name) && value.trim() !== '') {
            setFaltantes(prev => prev.filter(f => f !== name));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImagemCapa(file);
            setImagemPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validação no front
        const obrigatorios = [
            'nome', 'gerencia_id', 'endereco', 'municipio_id', 'latitude', 'longitude',
            'vazao_atual_ls', 'vazao_futura_ls', 'populacao_atual', 'populacao_futura',
            'controle_vazao_entrada', 'controle_vazao_saida', 'corpo_hidrico', 'classe_rio'
        ];
        
        const erros = obrigatorios.filter(campo => !formData[campo] || String(formData[campo]).trim() === '');
        
        if (erros.length > 0) {
            setFaltantes(erros);
            showError("Erro de Validação", "Por favor, preencha todos os campos obrigatórios marcados com *.");
            return;
        }

        setIsLoading(true);
        
        try {
            const data = new FormData();
            Object.keys(formData).forEach(key => {
                data.append(key, formData[key]);
            });
            
            if (imagemCapa) {
                data.append('imagem_capa', imagemCapa);
            }

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/empreendimentos`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: data
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Erro ao salvar ETE.');
            }

            showSuccess("Sucesso!", "Nova ETE cadastrada.");
            onSaveSuccess();
            onClose();
        } catch (error) {
            showError("Erro", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fadeIn pointer-events-none">
            <div className="bg-white pointer-events-auto rounded-[2rem] shadow-[0_0_60px_rgba(0,0,0,0.3)] w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col border border-slate-200">

                {/* HEADER */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50 shrink-0">
                    <div>
                        <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                            <span className="text-2xl">🏗️</span> Nova ETE
                        </h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                            Cadastro de Empreendimento
                        </p>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-red-50 hover:text-red-500 text-slate-400 transition-all text-xl font-bold">✕</button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-8 overflow-y-auto custom-scrollbar bg-white">

                    {/* 1. IDENTIDADE E LOCALIZAÇÃO */}
                    <section className="space-y-4">
                        <h3 className="text-[11px] font-black text-sabesp-blue uppercase tracking-[0.2em] border-l-4 border-sabesp-blue pl-3">Identidade & Local</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">

                            {/* Capa */}
                            <div className="md:col-span-1 bg-slate-50 p-4 rounded-2xl border-2 border-dashed border-slate-200">
                                <label className="label-form">Capa do Card</label>
                                <div className="flex items-center gap-4 mt-2">
                                    <div className="w-14 h-14 bg-white rounded-xl border overflow-hidden shrink-0 shadow-sm relative group">
                                        {imagemPreview ? (
                                            <img src={imagemPreview} alt="Capa" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">Sem img</div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="capa-upload" />
                                        <label htmlFor="capa-upload" className="text-[10px] bg-white border border-slate-200 text-slate-600 px-3 py-2 rounded-lg font-bold cursor-pointer hover:bg-slate-100 block text-center shadow-sm">
                                            📷 Escolher
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Dados Principais */}
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-form">Nome da ETE / Obra <span className="text-red-500 ml-1">*</span></label>
                                    <input name="nome" value={formData.nome} onChange={handleInputChange} className={`input-form font-bold text-slate-700 ${faltantes.includes('nome') ? '!border-red-500' : ''}`} placeholder="Ex: ETE Barueri" />
                                </div>
                                <div>
                                    <label className="label-form">Gerência Responsável <span className="text-red-500 ml-1">*</span></label>
                                    <select name="gerencia_id" value={formData.gerencia_id} onChange={handleInputChange} className={`input-form cursor-pointer ${faltantes.includes('gerencia_id') ? '!border-red-500' : ''}`}>
                                        <option value="">Selecione...</option>
                                        {listaGerencias.map(g => (
                                            <option key={g.id} value={g.id}>{g.nome}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Localização Completa */}
                                <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-3">
                                        <label className="label-form">Endereço Completo <span className="text-red-500 ml-1">*</span></label>
                                        <input name="endereco" value={formData.endereco} onChange={handleInputChange} className={`input-form ${faltantes.includes('endereco') ? '!border-red-500' : ''}`} placeholder="Rua, Número, Bairro, Cidade..." />
                                    </div>

                                    <div>
                                        <label className="label-form">Município <span className="text-red-500 ml-1">*</span></label>
                                        <select name="municipio_id" value={formData.municipio_id} onChange={handleInputChange} className={`input-form cursor-pointer ${faltantes.includes('municipio_id') ? '!border-red-500' : ''}`}>
                                            <option value="">Selecione...</option>
                                            {municipiosDB.map(m => (
                                                <option key={m.id} value={m.id}>{m.nome}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="label-form">Latitude <span className="text-red-500 ml-1">*</span></label>
                                        <input name="latitude" type="number" step="any" value={formData.latitude} onChange={handleInputChange} className={`input-form font-mono text-xs ${faltantes.includes('latitude') ? '!border-red-500' : ''}`} placeholder="-23.xxxx" />
                                    </div>
                                    <div>
                                        <label className="label-form">Longitude <span className="text-red-500 ml-1">*</span></label>
                                        <input name="longitude" type="number" step="any" value={formData.longitude} onChange={handleInputChange} className={`input-form font-mono text-xs ${faltantes.includes('longitude') ? '!border-red-500' : ''}`} placeholder="-46.xxxx" />
                                    </div>
                                    <div className="md:col-span-3 text-[10px] text-sabesp-blue bg-blue-50/50 p-2 rounded-lg font-bold border border-blue-100 flex items-center gap-2 mt-2">
                                        <span>💡</span> Dica: Você pode clicar no mapa para preencher a Latitude e Longitude automaticamente.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 2. ENGENHARIA */}
                    <section className="space-y-4">
                        <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-l-4 border-slate-200 pl-3">Engenharia & Processos</h3>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <div>
                                <label className="label-form text-slate-500">Vazão Atual <span className="text-red-500 ml-1">*</span></label>
                                <input name="vazao_atual_ls" type="number" step="any" value={formData.vazao_atual_ls} onChange={handleInputChange} className={`input-form bg-white ${faltantes.includes('vazao_atual_ls') ? '!border-red-500' : ''}`} placeholder="L/s" />
                            </div>
                            <div>
                                <label className="label-form text-slate-500">Vazão Futura <span className="text-red-500 ml-1">*</span></label>
                                <input name="vazao_futura_ls" type="number" step="any" value={formData.vazao_futura_ls} onChange={handleInputChange} className={`input-form bg-white ${faltantes.includes('vazao_futura_ls') ? '!border-red-500' : ''}`} placeholder="L/s" />
                            </div>
                            <div>
                                <label className="label-form text-slate-500">População Atual <span className="text-red-500 ml-1">*</span></label>
                                <input name="populacao_atual" type="number" value={formData.populacao_atual} onChange={handleInputChange} className={`input-form bg-white ${faltantes.includes('populacao_atual') ? '!border-red-500' : ''}`} placeholder="Habitantes" />
                            </div>
                            <div>
                                <label className="label-form text-slate-500">População Futura <span className="text-red-500 ml-1">*</span></label>
                                <input name="populacao_futura" type="number" value={formData.populacao_futura} onChange={handleInputChange} className={`input-form bg-white ${faltantes.includes('populacao_futura') ? '!border-red-500' : ''}`} placeholder="Habitantes" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="label-form">Controle Entrada <span className="text-red-500 ml-1">*</span></label>
                                <select name="controle_vazao_entrada" value={formData.controle_vazao_entrada} onChange={handleInputChange} className={`input-form cursor-pointer ${faltantes.includes('controle_vazao_entrada') ? '!border-red-500' : ''}`}>
                                    <option value="NI">Não Informado</option>
                                    <option value="Sim">Sim</option>
                                    <option value="Não">Não</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-form">Controle Saída <span className="text-red-500 ml-1">*</span></label>
                                <select name="controle_vazao_saida" value={formData.controle_vazao_saida} onChange={handleInputChange} className={`input-form cursor-pointer ${faltantes.includes('controle_vazao_saida') ? '!border-red-500' : ''}`}>
                                    <option value="NI">Não Informado</option>
                                    <option value="Sim">Sim</option>
                                    <option value="Não">Não</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-form">Exibir Planejamento Público</label>
                                <select name="exibir_planejamento_publico" value={formData.exibir_planejamento_publico} onChange={handleInputChange} className="input-form cursor-pointer font-bold text-sabesp-blue">
                                    <option value="1">Sim (Visível para o público)</option>
                                    <option value="0">Não (Oculto para o público)</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="label-form">Corpo Hídrico <span className="text-red-500 ml-1">*</span></label>
                                <input name="corpo_hidrico" value={formData.corpo_hidrico} onChange={handleInputChange} className={`input-form ${faltantes.includes('corpo_hidrico') ? '!border-red-500' : ''}`} placeholder="Ex: Rio Tietê" />
                            </div>
                            <div>
                                <label className="label-form">Classe do Rio <span className="text-red-500 ml-1">*</span></label>
                                <select name="classe_rio" value={formData.classe_rio} onChange={handleInputChange} className={`input-form ${faltantes.includes('classe_rio') ? '!border-red-500' : ''}`}>
                                    <option value="">Selecione...</option>
                                    <option value="Classe 1">Classe 1</option>
                                    <option value="Classe 2">Classe 2</option>
                                    <option value="Classe 3">Classe 3</option>
                                    <option value="Classe 4">Classe 4</option>
                                    <option value="Não Informado">Não Informado</option>
                                </select>
                            </div>
                        </div>
                    </section>

                    {/* 3. TEXTOS */}
                    <section className="space-y-4">
                        <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-l-4 border-slate-200 pl-3">Destaques e Benefícios</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="label-form">Diferenciais Técnicos</label>
                                <textarea name="texto_diferenciais" value={formData.texto_diferenciais} onChange={handleInputChange} className="input-form h-28 resize-none leading-relaxed" placeholder="- Tecnologia&#10;- Eficiência" />
                            </div>
                            <div>
                                <label className="label-form">Benefícios para a População</label>
                                <textarea name="texto_beneficios" value={formData.texto_beneficios} onChange={handleInputChange} className="input-form h-28 resize-none leading-relaxed" placeholder="- Saúde&#10;- Meio Ambiente" />
                            </div>
                            <div>
                                <label className="label-form">Sustentabilidade</label>
                                <textarea name="texto_sustentabilidade" value={formData.texto_sustentabilidade} onChange={handleInputChange} className="input-form h-28 resize-none leading-relaxed" placeholder="- Reciclagem&#10;- Preservação" />
                            </div>
                        </div>
                    </section>

                    {/* 4. INSTITUCIONAL */}
                    <section className="space-y-4">
                        <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-l-4 border-slate-200 pl-3">Conteúdo Institucional</h3>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="label-form">Descrição Curta (Abaixo do título principal)</label>
                                <textarea name="descricao_curta" value={formData.descricao_curta} onChange={handleInputChange} className="input-form h-20 resize-none" />
                            </div>
                            <div>
                                <label className="label-form">Descrição Completa</label>
                                <textarea name="descricao_completa" value={formData.descricao_completa} onChange={handleInputChange} className="input-form h-28 resize-none" placeholder="Descrição detalhada para a visão geral..." />
                            </div>
                            <div>
                                <label className="label-form">Concepção de Tratamento</label>
                                <textarea name="historia_narrativa" value={formData.historia_narrativa} onChange={handleInputChange} className="input-form h-32 resize-none" />
                            </div>
                        </div>
                    </section>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 shrink-0">
                        <button type="button" onClick={onClose} className="px-6 py-3 text-slate-400 font-bold hover:text-slate-600 transition-colors uppercase text-[10px] tracking-widest">Cancelar</button>
                        <button type="submit" disabled={isLoading} className="px-10 py-3 bg-slate-900 text-white font-black rounded-2xl hover:bg-sabesp-blue transition-all shadow-xl disabled:opacity-50 flex items-center gap-2 transform hover:-translate-y-1">
                            {isLoading ? 'Salvando...' : '💾 Salvar Nova ETE'}
                        </button>
                    </div>

                </form>
            </div>

            <style>{`
                .label-form { display: block; font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; margin-left: 0.2rem; }
                .input-form { width: 100%; padding: 0.7rem 1rem; background-color: #f8fafc; border: 2px solid #f1f5f9; border-radius: 0.75rem; font-size: 0.8rem; color: #334155; font-weight: 600; outline: none; transition: all 0.2s; }
                .input-form:focus { border-color: #00AEEF; background-color: #fff; box-shadow: 0 4px 12px -2px rgba(0, 174, 239, 0.15); color: #0f172a; }
                .input-form.\\!border-red-500 { border-color: #ef4444 !important; background-color: #fef2f2 !important; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            `}</style>
        </div>
    );
};

export default NovaETEModal;
